import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  TimePicker,
  Upload,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
  EditOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { GoogleMap } from '@react-google-maps/api';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import Marker from 'components/Basic/Marker';
import toast from 'components/Basic/Toast';
import {
  useCreateMcDeliveryPointEventMutation,
  useGetMcDeliveryPointEventsQuery,
  useLazyDownloadMcDeliveryPointQrQuery,
  useLazyGetMcDeliveryPointReportQuery,
  useUpdateMcDeliveryPointEventMutation,
  useUpdateMcDeliveryPointStatusMutation,
  useUploadMcDeliveryPointLogoMutation,
} from 'core/mcdeliveryPoints/McDeliveryPointsService';
import { useGetRestaurantsForDealsQuery } from 'core/restaurant/RestaurantService';
import { getCurrency, getLocations } from 'utilities/common';
import {
  formatEventDate,
  getEventTimeZone,
  toEventISOString,
  toEventPickerDate,
} from 'core/mcdeliveryPoints/eventTime';
import useEventReport from 'core/mcdeliveryPoints/useEventReport';
import { getLogoFileError } from 'core/mcdeliveryPoints/logoValidation';

const { RangePicker } = DatePicker;
const TIME_FORMAT = 'HH:mm';
const weekdayIds = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const formStepIds = {
  setup: 'Setup',
  date: 'Dates',
  logo: 'Logo',
  geo: 'Location',
};
const fieldsByStep = {
  setup: ['name', 'loyaltyFactor', 'description', 'address'],
  date: ['activeWeekdays', 'schedule', 'dailyStartTime', 'dailyEndTime'],
  logo: [],
  geo: ['latitude', 'longitude', 'radiusMeters', 'restaurantId'],
};

const McDeliveryPoints = () => {
  const intl = useIntl();
  const t = useCallback((id, values) => intl.formatMessage({ id }, values), [intl]);
  const [form] = Form.useForm();
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [sortFilter, setSortFilter] = useState('start-desc');
  const [appliedFilters, setAppliedFilters] = useState({
    status: 'all',
    startDate: null,
    endDate: null,
    sort: 'start-desc',
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [previewEventName, setPreviewEventName] = useState('');
  const [formVisible, setFormVisible] = useState(false);
  const [activeFormStep, setActiveFormStep] = useState('setup');
  const currency = getCurrency();
  const isElSalvador = process.env.REACT_APP_COUNTRY === 'sv';
  const weekdays = weekdayIds.map((id, value) => ({ label: t(id), value }));
  const formSteps = Object.entries(formStepIds).map(([key, id]) => ({ key, label: t(id) }));

  const { data: events = [], isFetching } = useGetMcDeliveryPointEventsQuery({ search });
  const { data: restaurantsData } = useGetRestaurantsForDealsQuery({});
  const [createEvent, { isLoading: isCreating }] = useCreateMcDeliveryPointEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateMcDeliveryPointEventMutation();
  const [updateStatus] = useUpdateMcDeliveryPointStatusMutation();
  const [uploadLogo, { isLoading: isUploadingLogo }] = useUploadMcDeliveryPointLogoMutation();
  const [downloadQr] = useLazyDownloadMcDeliveryPointQrQuery();
  const [getReport] = useLazyGetMcDeliveryPointReportQuery();
  const eventReport = useEventReport(getReport, () => toast.error({ title: t('The report could not be loaded') }));
  const { report } = eventReport;
  const eventTimeZone = getEventTimeZone();

  const latitude = Form.useWatch('latitude', form);
  const longitude = Form.useWatch('longitude', form);
  const activeWeekdays = Form.useWatch('activeWeekdays', form);
  const defaultLocation = getLocations();
  const mapCenter = useMemo(
    () => ({
      lat: typeof latitude === 'number' ? latitude : defaultLocation.lat,
      lng: typeof longitude === 'number' ? longitude : defaultLocation.lng,
    }),
    [defaultLocation.lat, defaultLocation.lng, latitude, longitude],
  );
  const activeFormStepIndex = formSteps.findIndex(step => step.key === activeFormStep);

  const restaurantOptions = useMemo(
    () =>
      (restaurantsData?.restaurants || []).map(restaurant => ({
        value: restaurant.id,
        label: `${restaurant.id} - ${restaurant.description || t('Restaurant')}`,
      })),
    [restaurantsData, t],
  );

  const filteredEvents = useMemo(() => {
    const result = events.filter(event => {
      if (appliedFilters.status === 'active' && !event.isActive) return false;
      if (appliedFilters.status === 'inactive' && event.isActive) return false;
      if (
        appliedFilters.startDate &&
        toEventPickerDate(event.startAt).isBefore(appliedFilters.startDate.startOf('day'))
      ) {
        return false;
      }
      if (appliedFilters.endDate && toEventPickerDate(event.endAt).isAfter(appliedFilters.endDate.endOf('day'))) {
        return false;
      }
      return true;
    });

    return [...result].sort((left, right) => {
      switch (appliedFilters.sort) {
        case 'start-asc':
          return dayjs(left.startAt).valueOf() - dayjs(right.startAt).valueOf();
        case 'name-asc':
          return left.name.localeCompare(right.name);
        case 'transactions-desc':
          return (right.transactions || 0) - (left.transactions || 0);
        default:
          return dayjs(right.startAt).valueOf() - dayjs(left.startAt).valueOf();
      }
    });
  }, [appliedFilters, events]);

  const handleSearchChange = event => {
    const value = event.target.value;
    setSearchText(value);
    if (!value) setSearch('');
  };

  const handleSearchKeyDown = event => {
    if (event.key === 'Enter') setSearch(searchText.trim());
  };

  const applyFilters = () => {
    if (startDateFilter && endDateFilter && startDateFilter.isAfter(endDateFilter, 'day')) {
      toast.error({ title: t('The start date cannot be after the end date') });
      return;
    }
    setSearch(searchText.trim());
    setAppliedFilters({
      status: statusFilter,
      startDate: startDateFilter,
      endDate: endDateFilter,
      sort: sortFilter,
    });
  };

  const resetFilters = () => {
    setSearch('');
    setSearchText('');
    setStatusFilter('all');
    setStartDateFilter(null);
    setEndDateFilter(null);
    setSortFilter('start-desc');
    setAppliedFilters({
      status: 'all',
      startDate: null,
      endDate: null,
      sort: 'start-desc',
    });
  };

  const openForm = event => {
    setSelectedEvent(event || null);
    setLogo(null);
    setLogoPreview(event?.logoUrl || null);
    setPreviewEventName(event?.name || '');
    setActiveFormStep('setup');
    form.resetFields();
    if (event) {
      form.setFieldsValue({
        ...event,
        schedule: [toEventPickerDate(event.startAt), toEventPickerDate(event.endAt)],
        dailyStartTime: dayjs(`2000-01-01T${event.dailyStartTime}:00`),
        dailyEndTime: dayjs(`2000-01-01T${event.dailyEndTime}:00`),
      });
    } else {
      form.setFieldsValue({
        activeWeekdays: [0, 1, 2, 3, 4, 5, 6],
        loyaltyFactor: 1,
        radiusMeters: 100,
        isActive: true,
        latitude: defaultLocation.lat,
        longitude: defaultLocation.lng,
      });
    }
    setFormVisible(true);
  };

  const moveToStep = nextStep => {
    form.setFields(fieldsByStep[activeFormStep].map(name => ({ name, errors: [] })));
    setActiveFormStep(nextStep);
  };

  const moveToNextStep = async () => {
    try {
      await form.validateFields(fieldsByStep[activeFormStep]);
      const nextStep = formSteps[activeFormStepIndex + 1];
      if (nextStep) setActiveFormStep(nextStep.key);
    } catch (error) {
      // Ant Design displays the validation errors for the current section.
    }
  };

  const moveToPreviousStep = () => {
    const previousStep = formSteps[activeFormStepIndex - 1];
    if (previousStep) setActiveFormStep(previousStep.key);
  };

  const handleFormFinishFailed = ({ errorFields }) => {
    const firstInvalidField = errorFields[0]?.name?.[0];
    const invalidStep = formSteps.find(step => fieldsByStep[step.key].includes(firstInvalidField));
    if (invalidStep) setActiveFormStep(invalidStep.key);
  };

  const save = async values => {
    const completeValues = { ...form.getFieldsValue(true), ...values };
    if (
      !Array.isArray(completeValues.schedule) ||
      completeValues.schedule.length !== 2 ||
      !completeValues.dailyStartTime ||
      !completeValues.dailyEndTime
    ) {
      setActiveFormStep('date');
      toast.error({ title: t('Complete the event dates and times') });
      return;
    }

    const body = {
      ...completeValues,
      startAt: toEventISOString(completeValues.schedule[0]),
      endAt: toEventISOString(completeValues.schedule[1]),
      dailyStartTime: completeValues.dailyStartTime.format(TIME_FORMAT),
      dailyEndTime: completeValues.dailyEndTime.format(TIME_FORMAT),
    };
    delete body.schedule;

    let saved;
    try {
      saved = selectedEvent
        ? await updateEvent({ id: selectedEvent.id, body }).unwrap()
        : await createEvent(body).unwrap();
      if (!selectedEvent) setSelectedEvent(saved);
    } catch (error) {
      toast.error({ title: t('The event could not be saved') });
      return;
    }

    if (logo) {
      try {
        await uploadLogo({ id: saved.id, logo }).unwrap();
      } catch (error) {
        toast.error({ title: t('The event was saved, but its logo could not be uploaded') });
        setFormVisible(false);
        return;
      }
    }

    toast.success({ title: t('Event saved successfully') });
    setFormVisible(false);
  };

  const changeStatus = async (event, isActive) => {
    try {
      await updateStatus({ id: event.id, isActive }).unwrap();
    } catch (error) {
      toast.error({ title: t('The event status could not be changed') });
    }
  };

  const handleDownloadQr = async event => {
    try {
      const blob = await downloadQr(event.id).unwrap();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `mc-delivery-point-${event.id}.png`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error({ title: t('The QR code could not be downloaded') });
    }
  };

  const deactivateEvent = event => {
    Modal.confirm({
      className: 'mc_delivery_points_confirm',
      title: t('Deactivate event'),
      content: t('Do you want to deactivate {name}?', { name: event.name }),
      okText: t('Deactivate'),
      cancelText: t('Cancel'),
      okButtonProps: { danger: true },
      onOk: () => changeStatus(event, false),
    });
  };

  const columns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      width: 100,
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      render: name => <span className="mc_delivery_points_event_name">{name}</span>,
    },
    {
      title: t('Date'),
      render: event => (
        <div className="mc_delivery_points_stacked_value">
          <span>{formatEventDate(event.startAt, 'DD/MM/YYYY')}</span>
          <span>{formatEventDate(event.endAt, 'DD/MM/YYYY')}</span>
        </div>
      ),
    },
    {
      title: t('Time'),
      render: event => (
        <div className="mc_delivery_points_stacked_value">
          <span>{event.dailyStartTime}</span>
          <span>{event.dailyEndTime}</span>
        </div>
      ),
    },
    {
      title: t('Transactions'),
      dataIndex: 'transactions',
      render: (value, event) => (
        <button type="button" className="mc_delivery_points_transactions" onClick={() => eventReport.open(event)}>
          {value || 0}
        </button>
      ),
    },
    {
      title: t('Actions'),
      render: event => (
        <Space className="mc_delivery_points_actions" size={8}>
          <Button
            className="mc_delivery_points_action_button mc_delivery_points_action_button_icon"
            icon={<QrcodeOutlined />}
            aria-label={t('Download QR')}
            title={t('Download QR')}
            onClick={() => handleDownloadQr(event)}
          />
          <Button
            className="mc_delivery_points_action_button mc_delivery_points_action_button_icon"
            icon={<EditOutlined />}
            aria-label={t('Edit event')}
            title={t('Edit event')}
            onClick={() => openForm(event)}
          />
          <Button
            className="mc_delivery_points_action_button mc_delivery_points_action_button_icon"
            icon={<DeleteOutlined />}
            aria-label={t('Deactivate event')}
            title={t('Deactivate event')}
            disabled={!event.isActive}
            onClick={() => deactivateEvent(event)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="mc_delivery_points_page">
      <h1 className="mc_delivery_points_title">{t('McDelivery Point')}</h1>

      <Button className="mc_delivery_points_create" onClick={() => openForm()}>
        {t('Add new event')} <ArrowRightOutlined />
      </Button>

      <div className="mc_delivery_points_toolbar">
        <div className="mc_delivery_points_filters">
          <Select
            className="mc_delivery_points_filter_control mc_delivery_points_status_filter"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: t('All') },
              { value: 'active', label: t('Active') },
              { value: 'inactive', label: t('Inactive') },
            ]}
          />
          <DatePicker
            className="mc_delivery_points_filter_control"
            placeholder={t('Start date')}
            value={startDateFilter}
            onChange={setStartDateFilter}
            disabledDate={current => Boolean(endDateFilter && current?.isAfter(endDateFilter, 'day'))}
          />
          <DatePicker
            className="mc_delivery_points_filter_control"
            placeholder={t('End date')}
            value={endDateFilter}
            onChange={setEndDateFilter}
            disabledDate={current => Boolean(startDateFilter && current?.isBefore(startDateFilter, 'day'))}
          />
          <Select
            className="mc_delivery_points_filter_control mc_delivery_points_sort_filter"
            value={sortFilter}
            onChange={setSortFilter}
            options={[
              { value: 'start-desc', label: t('Most recent') },
              { value: 'start-asc', label: t('Oldest') },
              { value: 'name-asc', label: t('Name') },
              { value: 'transactions-desc', label: t('Transactions') },
            ]}
          />
          <div className="mc_delivery_points_filter_actions">
            <Button className="mc_delivery_points_apply_filters" onClick={applyFilters}>
              {t('Apply filters')}
            </Button>
            <Button
              className="mc_delivery_points_reset_filters"
              icon={<ReloadOutlined />}
              aria-label={t('Clear filters')}
              title={t('Clear filters')}
              onClick={resetFilters}
            />
          </div>
        </div>

        <Input
          className="mc_delivery_points_search"
          suffix={<SearchOutlined onClick={applyFilters} />}
          placeholder={t('Search event')}
          allowClear
          value={searchText}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
        />
      </div>

      <Table
        className="mc_delivery_points_table"
        rowKey="id"
        loading={isFetching}
        dataSource={filteredEvents}
        columns={columns}
        rowClassName={event => (event.isActive ? 'mc_delivery_points_row_active' : 'mc_delivery_points_row_inactive')}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: total => t('Results: {total}', { total }),
        }}
      />

      <Modal
        wrapClassName="mc_delivery_points_modal"
        title={selectedEvent ? t('Edit event') : t('New event')}
        open={formVisible}
        onCancel={() => setFormVisible(false)}
        footer={null}
        width={610}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} onFinishFailed={handleFormFinishFailed}>
          <div className="mc_delivery_points_form_tabs" role="tablist" aria-label={t('Event sections')}>
            {formSteps.map((step, index) => (
              <button
                key={step.key}
                type="button"
                role="tab"
                aria-selected={activeFormStep === step.key}
                className={`mc_delivery_points_form_tab ${activeFormStep === step.key ? 'active' : ''} ${
                  index < activeFormStepIndex ? 'complete' : ''
                }`}
                onClick={() => moveToStep(step.key)}
              >
                {step.label}
              </button>
            ))}
          </div>

          <div className="mc_delivery_points_form_section">
            <div className={`mc_delivery_points_form_step ${activeFormStep === 'setup' ? 'active' : ''}`}>
              <Form.Item
                name="name"
                label={t('Event name')}
                rules={[
                  { required: true, message: t('Enter the event name') },
                  { max: 50, message: t('The event name cannot exceed 50 characters') },
                ]}
              >
                <Input maxLength={50} onChange={event => setPreviewEventName(event.target.value)} />
              </Form.Item>
              {!isElSalvador && (
                <Form.Item
                  name="loyaltyFactor"
                  label={t('Loyalty factor')}
                  rules={[{ required: true, message: t('Select the loyalty factor') }]}
                >
                  <Select
                    options={[
                      { value: 0, label: t('No') },
                      { value: 1, label: '1' },
                      { value: 1.5, label: '1.5' },
                      { value: 2, label: '2' },
                    ]}
                  />
                </Form.Item>
              )}
              <Form.Item name="description" label={t('Event description')}>
                <Input.TextArea rows={4} showCount maxLength={500} />
              </Form.Item>
              <Form.Item
                name="address"
                label={t('Event address')}
                rules={[{ required: true, message: t('Enter the event address') }]}
              >
                <Input />
              </Form.Item>
            </div>

            <div className={`mc_delivery_points_form_step ${activeFormStep === 'date' ? 'active' : ''}`}>
              <Form.Item label={t('Active days')} required>
                <Checkbox
                  checked={activeWeekdays?.length === weekdays.length}
                  indeterminate={activeWeekdays?.length > 0 && activeWeekdays?.length < weekdays.length}
                  onChange={event =>
                    form.setFieldValue('activeWeekdays', event.target.checked ? weekdays.map(day => day.value) : [])
                  }
                >
                  {t('Every day')}
                </Checkbox>
                <Form.Item
                  name="activeWeekdays"
                  noStyle
                  rules={[{ required: true, message: t('Select at least one day') }]}
                >
                  <Checkbox.Group className="mc_delivery_points_weekdays" options={weekdays} />
                </Form.Item>
              </Form.Item>
              <Form.Item
                name="schedule"
                label={t('Event start and end')}
                extra={t('Event timezone: {timezone}', { timezone: eventTimeZone })}
                rules={[{ required: true, message: t('Select the dates') }]}
              >
                <RangePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="dailyStartTime" label={t('Daily start time')} rules={[{ required: true }]}>
                    <TimePicker format={TIME_FORMAT} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dailyEndTime" label={t('Daily end time')} rules={[{ required: true }]}>
                    <TimePicker format={TIME_FORMAT} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div className={`mc_delivery_points_form_step ${activeFormStep === 'logo' ? 'active' : ''}`}>
              <Form.Item label={t('Event logo')}>
                <Upload
                  maxCount={1}
                  beforeUpload={file => {
                    const fileError = getLogoFileError(file);
                    if (fileError) {
                      toast.error({ title: t(fileError) });
                      return Upload.LIST_IGNORE;
                    }

                    return new Promise(resolve => {
                      const reader = new FileReader();
                      reader.onerror = () => {
                        toast.error({ title: t('The logo could not be read') });
                        resolve(Upload.LIST_IGNORE);
                      };
                      reader.onload = event => {
                        const image = new Image();
                        image.onerror = () => {
                          toast.error({ title: t('The logo could not be read') });
                          resolve(Upload.LIST_IGNORE);
                        };
                        image.onload = () => {
                          if (image.width > 1200 || image.height > 1200) {
                            toast.error({ title: t('The logo cannot exceed 1200 by 1200 pixels') });
                            resolve(Upload.LIST_IGNORE);
                            return;
                          }
                          setLogo(file);
                          setLogoPreview(event.target.result);
                          resolve(false);
                        };
                        image.src = event.target.result;
                      };
                      reader.readAsDataURL(file);
                    });
                  }}
                  onRemove={() => {
                    setLogo(null);
                    setLogoPreview(selectedEvent?.logoUrl || null);
                  }}
                  accept="image/png,image/jpeg"
                  listType="picture"
                >
                  <Button className="mc_delivery_points_upload_button">{t('Select image')}</Button>
                </Upload>
                <p className="mc_delivery_points_upload_hint">
                  {t('JPG or PNG format. Maximum 1200 by 1200 pixels and 2 MB.')}
                </p>
              </Form.Item>
              <div className="mc_delivery_points_event_preview">
                {logoPreview ? <img src={logoPreview} alt={t('Event logo')} /> : <div className="logo_placeholder" />}
                <div>
                  <strong>{previewEventName || t('Event name')}</strong>
                  <span>{t('Order now')}</span>
                </div>
                <ArrowRightOutlined />
              </div>
            </div>

            <div className={`mc_delivery_points_form_step ${activeFormStep === 'geo' ? 'active' : ''}`}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="latitude"
                    label={t('Latitude')}
                    rules={[{ required: true, message: t('Enter the latitude') }]}
                  >
                    <InputNumber min={-90} max={90} step={0.000001} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="longitude"
                    label={t('Longitude')}
                    rules={[{ required: true, message: t('Enter the longitude') }]}
                  >
                    <InputNumber min={-180} max={180} step={0.000001} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label={t('Point on the map')}>
                <GoogleMap
                  mapContainerClassName="mc_delivery_points_map"
                  center={mapCenter}
                  zoom={14}
                  options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                  onClick={event =>
                    form.setFieldsValue({ latitude: event.latLng.lat(), longitude: event.latLng.lng() })
                  }
                >
                  <Marker lat={mapCenter.lat} lng={mapCenter.lng} />
                </GoogleMap>
              </Form.Item>
              <Form.Item
                name="radiusMeters"
                label={t('Radius (meters)')}
                rules={[{ required: true, message: t('Enter the radius') }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="restaurantId"
                label={t('Supplier restaurant')}
                rules={[{ required: true, message: t('Select a restaurant') }]}
              >
                <Select showSearch options={restaurantOptions} optionFilterProp="label" />
              </Form.Item>
            </div>
          </div>

          <div className="mc_delivery_points_step_navigation">
            {activeFormStepIndex > 0 ? (
              <Button
                type="text"
                className="mc_delivery_points_navigation_button"
                icon={<ArrowLeftOutlined />}
                onClick={moveToPreviousStep}
              >
                {t('Previous')}
              </Button>
            ) : (
              <span />
            )}
            {activeFormStepIndex < formSteps.length - 1 && (
              <Button type="text" className="mc_delivery_points_navigation_button" onClick={moveToNextStep}>
                {t('Next')} <ArrowRightOutlined />
              </Button>
            )}
          </div>

          <div className="mc_delivery_points_form_footer">
            <div className="mc_delivery_points_active_field">
              <span>{t('Active')}</span>
              <Form.Item name="isActive" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </div>
            <Space>
              <Button type="text" className="mc_delivery_points_cancel_button" onClick={() => setFormVisible(false)}>
                {t('Cancel')}
              </Button>
              <Button
                className="mc_delivery_points_submit"
                htmlType="submit"
                loading={isCreating || isUpdating || isUploadingLogo}
                disabled={activeFormStepIndex !== formSteps.length - 1}
              >
                {selectedEvent ? t('Save') : t('Create')} <ArrowRightOutlined />
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      <Modal
        title={t('Report: {name}', { name: eventReport.event?.name || '' })}
        open={Boolean(eventReport.event)}
        onCancel={eventReport.close}
        footer={null}
        width={780}
      >
        <Space style={{ marginBottom: 20 }}>
          <RangePicker value={eventReport.range} onChange={eventReport.changeRange} />
          <Button type="primary" loading={eventReport.isLoading} onClick={eventReport.load}>
            {t('View report')}
          </Button>
        </Space>
        {report && (
          <>
            <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
              <Descriptions.Item label={t('Transactions')}>{report.totals.transactions}</Descriptions.Item>
              <Descriptions.Item label={t('Sales')}>
                {currency.symbol}
                {(report.totals.sales / 100).toFixed(2)}
              </Descriptions.Item>
            </Descriptions>
            <Table
              rowKey="id"
              size="small"
              dataSource={report.orders}
              columns={[
                { title: t('Order'), dataIndex: 'order' },
                {
                  title: t('Date'),
                  dataIndex: 'createdAt',
                  render: value => formatEventDate(value, 'DD/MM/YYYY HH:mm'),
                },
                { title: t('Status'), dataIndex: 'state' },
                {
                  title: t('Total'),
                  dataIndex: 'totalPaymentAmount',
                  render: value => `${currency.symbol}${((value || 0) / 100).toFixed(2)}`,
                },
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default McDeliveryPoints;
